import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store';
import PostCard from '../components/post/PostCard';

interface User {
  id: string;
  displayName: string;
  handle: string;
  bio?: string;
  avatarUrl?: string;
  createdAt: string;
  _count: {
    posts: number;
    followers: number;
    following: number;
  };
  isFollowing?: boolean;
}

interface Post {
  id: string;
  content: string;
  authorId: string;
  createdAt: string;
  author: User;
  media: Array<{ id: string; type: string; url: string; thumbnailUrl?: string }>;
  segment?: { id: string; name: string; waterway: { id: string; name: string } };
  _count: { reactions: number; comments: number };
  userReaction?: string;
}

export default function ProfilePage() {
  const { identifier } = useParams();
  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'posts' | 'media' | 'likes'>('posts');
  const { user: currentUser } = useAuthStore();
  
  const isOwnProfile = currentUser?.handle === identifier || currentUser?.id === identifier;
  
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError('');
      
      try {
        const [userRes, postsRes] = await Promise.all([
          api.users.getProfile(identifier!),
          api.users.getUserPosts(identifier!),
        ]);
        setUser(userRes);
        setPosts(postsRes);
      } catch (err: any) {
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    
    if (identifier) loadProfile();
  }, [identifier]);
  
  const handleFollow = async () => {
    if (!user) return;
    
    try {
      if (user.isFollowing) {
        await api.users.unfollow(user.id);
        setUser((prev) =>
          prev
            ? { ...prev, isFollowing: false, _count: { ...prev._count, followers: prev._count.followers - 1 } }
            : null
        );
      } else {
        await api.users.follow(user.id);
        setUser((prev) =>
          prev
            ? { ...prev, isFollowing: true, _count: { ...prev._count, followers: prev._count.followers + 1 } }
            : null
        );
      }
    } catch (err) {
      console.error('Failed to follow/unfollow:', err);
    }
  };
  
  const handleReaction = async (postId: string, type: string) => {
    try {
      await api.posts.react(postId, { type });
      setPosts((prev) =>
        prev.map((post) => {
          if (post.id === postId) {
            const hadReaction = !!post.userReaction;
            const sameReaction = post.userReaction === type;
            return {
              ...post,
              userReaction: sameReaction ? undefined : type,
              _count: {
                ...post._count,
                reactions: post._count.reactions + (sameReaction ? -1 : hadReaction ? 0 : 1),
              },
            };
          }
          return post;
        })
      );
    } catch (err) {
      console.error('Failed to react:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="animate-pulse">
          <div className="h-32 bg-slate-200 dark:bg-slate-700 rounded-t-xl" />
          <div className="card rounded-t-none p-4">
            <div className="flex items-end -mt-16 mb-4">
              <div className="w-24 h-24 rounded-full bg-slate-300 dark:bg-slate-600 border-4 border-white dark:border-slate-800" />
            </div>
            <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-40 mb-2" />
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24" />
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">User not found</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-6">{error || 'This user doesn\'t exist.'}</p>
        <Link to="/" className="btn btn-primary">Go home</Link>
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto pb-6">
      {/* Cover */}
      <div className="h-32 bg-gradient-to-r from-primary-400 to-primary-600" />
      
      {/* Profile header */}
      <div className="card rounded-t-none mx-4 -mt-4">
        <div className="p-4">
          <div className="flex items-end justify-between -mt-16 mb-4">
            <div className="avatar w-24 h-24 text-3xl border-4 border-white dark:border-slate-800 shadow-lg">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                user.displayName.charAt(0).toUpperCase()
              )}
            </div>
            
            {isOwnProfile ? (
              <Link to="/settings" className="btn btn-secondary">
                Edit profile
              </Link>
            ) : (
              <div className="flex gap-2">
                <Link to={`/chat?user=${user.id}`} className="btn btn-secondary">
                  <MessageIcon className="w-5 h-5" />
                </Link>
                <button
                  onClick={handleFollow}
                  className={user.isFollowing ? 'btn btn-secondary' : 'btn btn-primary'}
                >
                  {user.isFollowing ? 'Following' : 'Follow'}
                </button>
              </div>
            )}
          </div>
          
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">{user.displayName}</h1>
          <p className="text-slate-500 dark:text-slate-400">@{user.handle}</p>
          
          {user.bio && (
            <p className="mt-3 text-slate-700 dark:text-slate-300">{user.bio}</p>
          )}
          
          <div className="flex gap-6 mt-4 text-sm">
            <div>
              <span className="font-semibold text-slate-900 dark:text-white">{user._count.posts}</span>
              <span className="text-slate-500 dark:text-slate-400 ml-1">posts</span>
            </div>
            <button className="hover:underline">
              <span className="font-semibold text-slate-900 dark:text-white">{user._count.followers}</span>
              <span className="text-slate-500 dark:text-slate-400 ml-1">followers</span>
            </button>
            <button className="hover:underline">
              <span className="font-semibold text-slate-900 dark:text-white">{user._count.following}</span>
              <span className="text-slate-500 dark:text-slate-400 ml-1">following</span>
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-t border-slate-100 dark:border-slate-700">
          {(['posts', 'media', 'likes'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-primary-500 border-b-2 border-primary-500'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      {/* Posts */}
      <div className="px-4 mt-4 space-y-4">
        {posts.length === 0 ? (
          <div className="card p-8 text-center">
            <PostIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              {isOwnProfile ? "You haven't posted anything yet" : "No posts yet"}
            </p>
            {isOwnProfile && (
              <Link to="/create" className="btn btn-primary mt-4">Create your first post</Link>
            )}
          </div>
        ) : (
          posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onReact={handleReaction}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Icons
function MessageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function PostIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  );
}
